import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { TFundraiserState } from '@feathr/blackbox';
import type { TTheme } from '@feathr/components';
import { Text } from '@feathr/components';

import * as styles from './ConnectState.css';

interface IProps {
  state?: TFundraiserState;
}

function ConnectState({ state }: Readonly<IProps>): JSX.Element | null {
  const { t } = useTranslation();
  const {
    colors: {
      green: { 6: green600, 7: green700 },
      red: { 6: red600, 7: red700 },
      yellow: { 6: yellow600, 7: yellow700 },
    },
    spacing,
  } = useMantineTheme();

  // If the account is not connected, don't show any state indicator.
  if (state === 'not_connected' || !state) {
    return null;
  }

  const stateToIconColorMap = (state: TFundraiserState): TTheme => {
    const map: Record<Exclude<TFundraiserState, 'not_connected'>, string> = {
      connected: green600,
      failed: red600,
      pending: yellow600,
    } as const;

    return map[state];
  };

  const stateToTextColorMap = (state: TFundraiserState): TTheme => {
    const map: Record<Exclude<TFundraiserState, 'not_connected'>, string> = {
      connected: green700,
      failed: red700,
      pending: yellow700,
    } as const;

    return map[state];
  };

  const stateToTextMap = (state: TFundraiserState): string => {
    const map: Record<Exclude<TFundraiserState, 'not_connected'>, string> = {
      connected: t('Connected'),
      failed: t('Erroring'),
      pending: t('In progress'),
    } as const;

    return map[state];
  };

  return (
    <Flex align={'center'} gap={spacing['2']}>
      <FontAwesomeIcon className={styles.icon} color={stateToIconColorMap(state)} icon={faCircle} />
      <Text.DefaultRegular c={stateToTextColorMap(state)}>
        {stateToTextMap(state)}
      </Text.DefaultRegular>
    </Flex>
  );
}

export default ConnectState;
